import React, { useState } from 'react';
import { NTRP_QUIZ, UserProfile, saveUserProfile } from '../core/profile';
import { tennisAudio } from '../core/audio';
import { useI18n } from '../core/i18n';
import confetti from 'canvas-confetti';
import {
  Award,
  Sparkles,
  CheckCircle2,
  ChevronRight,
  RotateCcw,
  BarChart2,
  Trophy,
} from 'lucide-react';

interface NtrpAssessmentViewProps {
  profile: UserProfile;
  onUpdateProfile: (p: UserProfile) => void;
}

export const NtrpAssessmentView: React.FC<NtrpAssessmentViewProps> = ({ profile, onUpdateProfile }) => {
  const { t } = useI18n();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [calculatedRating, setCalculatedRating] = useState<number>(profile.ntrpRating);

  const currentQuestion = NTRP_QUIZ[currentStep];

  const handleSelectOption = (points: number) => {
    const nextAnswers = [...selectedAnswers, points];
    setSelectedAnswers(nextAnswers);
    tennisAudio.playHit('topspin');

    if (currentStep + 1 < NTRP_QUIZ.length) {
      setCurrentStep(currentStep + 1);
    } else {
      // Calculate average NTRP
      const sum = nextAnswers.reduce((a, b) => a + b, 0);
      const avg = Math.round((sum / nextAnswers.length) * 10) / 10;
      setCalculatedRating(avg);
      setIsCompleted(true);

      // Save to user profile & award EXP
      const nextProfile: UserProfile = {
        ...profile,
        ntrpRating: avg,
        exp: profile.exp + 100,
      };
      onUpdateProfile(nextProfile);
      saveUserProfile(nextProfile);

      tennisAudio.playCheer();
      confetti({ particleCount: 80, spread: 70 });
    }
  };

  const handleRestart = () => {
    setCurrentStep(0);
    setSelectedAnswers([]);
    setIsCompleted(false);
  };

  const getNtrpDescription = (rating: number) => {
    if (rating <= 2.0) {
      return {
        level: 'NTRP 2.0 - 初步球友 (Novice)',
        summary: '掌握基本准备姿势与简单推击，能在对方喂球下形成几个回合，尚未建立稳定旋转与变线意识。',
        recommendation: '建议重点练习大陆式握拍发球与半西方式正手刷旋，提高击球过网高度。'
      };
    } else if (rating <= 3.0) {
      return {
        level: 'NTRP 3.0 - 进阶好手 (Intermediate)',
        summary: '能打出中等速度的正手相持，基本能完成发球入界，但面对旋转球和快球时步伐调整欠缺。',
        recommendation: '建议加强分腿垫步（Split Step）与沃德洛第一法则（保持斜线深球），减少主动失误。'
      };
    } else if (rating <= 4.0) {
      return {
        level: 'NTRP 3.5 - 4.0 俱乐部主力 (Joy Club Ace)',
        summary: '掌握可靠的强上旋与切削，发球有落点控制，具备网前截击与高压能力，能根据比分制定战术。',
        recommendation: '建议专精 Serve + 1 发球抢攻套路，加强双打抢网（Poaching）与关键分抗压心法。'
      };
    } else {
      return {
        level: 'NTRP 4.5+ 巡回赛大师 (Master/Elite)',
        summary: '击球质量高、速度与旋转兼备，能把正手当作重炮武器使用，具备赛点/抢七大心脏心理素质。',
        recommendation: '保持体能与敏捷梯训练，深研针对不同对手风格（磨工、重炮手、网前截击者）的破局体系。'
      };
    }
  };

  const resultInfo = getNtrpDescription(calculatedRating);

  return (
    <div className="w-full max-w-3xl mx-auto p-3 sm:p-5 space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-850 to-slate-900 p-5 rounded-3xl border border-slate-800 shadow-xl flex items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500/20 to-lime-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400 shadow-glow-ball">
            <Award className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl font-black text-white flex items-center gap-2">
              <span>{t.ntrpAssessment}</span>
              <span className="text-[10px] bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full border border-amber-500/40 font-bold">
                USTA 标准对齐
              </span>
            </h1>
            <p className="text-xs text-slate-400 mt-0.5">
              客观评估你的底线相持、发球武器、网前截击与战术心理四大维度
            </p>
          </div>
        </div>

        <div className="text-xs font-mono font-bold text-lime-400 bg-slate-950 px-3 py-1.5 rounded-xl border border-slate-800">
          当前评级: NTRP {profile.ntrpRating.toFixed(1)}
        </div>
      </div>

      {/* Quiz Card */}
      {!isCompleted ? (
        <div className="bg-slate-900/90 p-5 sm:p-7 rounded-3xl border border-slate-800 shadow-2xl space-y-6">
          {/* Progress Bar */}
          <div className="space-y-1.5">
            <div className="flex justify-between text-xs text-slate-400">
              <span className="font-bold text-amber-400">第 {currentStep + 1} / {NTRP_QUIZ.length} 题</span>
              <span>进度: {Math.round(((currentStep + 1) / NTRP_QUIZ.length) * 100)}%</span>
            </div>
            <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-amber-500 to-lime-400 transition-all duration-300"
                style={{ width: `${((currentStep + 1) / NTRP_QUIZ.length) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Question Title */}
          <div>
            <h2 className="text-sm sm:text-base font-black text-white leading-snug">
              {currentQuestion.question}
            </h2>
            <div className="text-xs text-slate-400 font-mono mt-1">{currentQuestion.questionEn}</div>
          </div>

          {/* Options List */}
          <div className="space-y-2.5">
            {currentQuestion.options.map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleSelectOption(option.points)}
                className="w-full p-4 rounded-2xl bg-slate-950/80 hover:bg-slate-850 border border-slate-800 hover:border-lime-400/80 text-left transition-all group flex items-center justify-between"
              >
                <div className="space-y-1 pr-2">
                  <div className="text-xs font-bold text-white group-hover:text-lime-300 transition-colors">
                    {option.text}
                  </div>
                  <div className="text-[11px] text-slate-500">{option.textEn}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-lime-400 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      ) : (
        /* Result Card */
        <div className="bg-slate-900/90 p-6 sm:p-8 rounded-3xl border border-lime-500/40 shadow-2xl space-y-6 text-center animate-fadeIn">
          <div className="w-20 h-20 rounded-3xl bg-lime-500/20 border-2 border-lime-400 flex items-center justify-center text-4xl mx-auto shadow-glow-ball">
            🎾
          </div>

          <div>
            <span className="text-xs uppercase font-black tracking-widest text-lime-400">
              综合评估完成 · 荣获战力认证
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-white mt-1">
              NTRP {calculatedRating.toFixed(1)}
            </h2>
            <div className="text-sm font-bold text-amber-400 mt-1">{resultInfo.level}</div>
          </div>

          <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800 text-left space-y-2 text-xs">
            <div>
              <span className="text-slate-400 font-bold block mb-0.5">能力综述：</span>
              <p className="text-slate-200 leading-relaxed">{resultInfo.summary}</p>
            </div>
            <div className="pt-2 border-t border-slate-850">
              <span className="text-lime-400 font-bold block mb-0.5">教练进阶锦囊：</span>
              <p className="text-slate-300 leading-relaxed">{resultInfo.recommendation}</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={handleRestart}
              className="flex items-center space-x-1.5 px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-750 text-xs text-slate-300 border border-slate-700"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>重新评测</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
